import { AppBar, Tab, Tabs, Box, Typography, Button } from "@material-ui/core";
import { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { TabIndicatorChildren } from "./components/TabIndicatorChildren";
import { LoadingButton } from "./components/LoadingButton";
import { useStyles } from "./components/CodeTabsStyle";
import "./styles.css";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { usePrismTheme } from "./components/prismTheme";
import { BasisTheory } from "@basis-theory/basis-theory-js";
import { SuccessMessage } from "./components/SuccessMessage";
import { BasisTheoryEncryption } from "@basis-theory/basis-theory-js-encryption";
import { BasisTheoryBrowserEncryptionProvider } from "@basis-theory/basis-theory-js-encryption-browser";

const BT_API = "key_6pfpiwjgQ1V9kLwS8xrV7V";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
async function readImages(files, setValue) {
  var file = files[0];
  const reader = new FileReader();
  reader.onload = (event) => {
    setValue(event.target.result);
  };
  reader.readAsDataURL(file);
}

const defaultResult = { error: "try tokenizing a string." };

export default function App() {
  const [over, setOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [encryptYourself, setEncryptYourself] = useState(false);
  const [data, setData] = useState("");
  const [result, setResult] = useState(defaultResult);
  const [tab, setTab] = useState(0);
  const prismTheme = usePrismTheme();
  const [bt, setBt] = useState({});
  const [btEncryption, setBtEncryption] = useState({});
  const [providerKey, setProviderKey] = useState({});
  const [cek, setCek] = useState({});
  const [kek, setKek] = useState({});
  const browserProvider = new BasisTheoryBrowserEncryptionProvider();

  useEffect(() => {
    new BasisTheory().init(BT_API).then(async (pBT) => {
      setBt(pBT);
    });

    new BasisTheoryEncryption()
      .init([browserProvider])
      .then(async (btEncryption) => {
        setBtEncryption(btEncryption);

        const providerKey = await btEncryption.providerKeyService.getOrCreateKey(
          "encryptionKey",
          "BROWSER",
          "RSA"
        );
        setProviderKey(providerKey);
      });
  }, []);

  useEffect(() => {
    if (bt === {} || value === "" || !encryptYourself) {
      setData(value);
      return;
    }

    const chunkList = value.match(/.{1,100}/g);
    Promise.all(
      chunkList.map(async (chunk) => {
        const encryptedChunk = await btEncryption.encryptionService.encrypt(
          chunk,
          providerKey
        );

        setCek(encryptedChunk.cek);
        setKek(encryptedChunk.kek);

        return encryptedChunk.cipherText;
      })
    ).then((encryptedList) => {
      setData(encryptedList);
    });
  }, [value, bt, providerKey, encryptYourself]);

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };
  const onDrop = (acceptedFiles, rejections) => {
    if (rejections.length > 0) {
      setResult(
        "The file you selected is to large for our demo, pick a file under 100kb."
      );
      setTab(1);
      return;
    }
    readImages(acceptedFiles, setValue);
    setTab(0);
  };
  const onDrag = () => setOver(true);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDrag,
    noClick: true,
    maxSize: 100000
  });

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`
    };
  }
  const classes = useStyles();
  const codeString = `curl "https://api.basistheory.com/tokens" 
  -X "POST" 
  -D '{
    "data": "${data}"
    ${
      encryptYourself
        ? ` "encryption": {
          "cek": ${JSON.stringify(cek)},
          "kek": ${JSON.stringify(kek)}
        }
  }'`
        : `}}'`
    }`;

  const submit = useCallback(async () => {
    setLoading(true);
    const encryption = encryptYourself
      ? {
          cek,
          kek
        }
      : null;
    const response = await bt.tokens.create({
      type: "token",
      data,
      encryption
    });

    setTokenId(response.id);
    setResult(response);
    setTab(1);
    setLoading(false);
  }, [data, bt]);

  return (
    <div className="container" {...getRootProps()}>
      <div id="sample">
        {tab === 1 ? (
          <SuccessMessage tokenId={tokenId} tokenizeAgain={() => setTab(0)} />
        ) : (
          <div id="form">
            <div
              className={
                isDragActive ? "row row-input active" : "row row-input"
              }
            >
              <textarea
                id="name"
                rows="6"
                className="texttotokenize"
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                  setTab(0);
                }}
                placeholder={
                  isDragActive
                    ? "Drop the files here ..."
                    : "Tell us your favorite color... or drop a file!"
                }
              ></textarea>
              <input {...getInputProps()} />
            </div>
            <div class="row">
              <input
                type="checkbox"
                id="encrypt_yourself"
                checked={encryptYourself}
                onChange={(event) => {
                  setEncryptYourself(!encryptYourself);
                }}
              />
              <label htmlFor="encrypt_yourself">
                Encrypt yourself so Basis Theory can't see it
              </label>
            </div>
            <LoadingButton
              id="submit_button"
              onClick={submit}
              className="submit"
              type="button"
              loading={loading}
            >
              Tokenize
            </LoadingButton>
          </div>
        )}
        <div className="results">
          <AppBar position="relative" className="tabbar">
            <Tabs
              centered
              indicatorColor="primary"
              TabIndicatorProps={{ children: <TabIndicatorChildren /> }}
              value={tab}
              onChange={handleChange}
              className={classes.tabs}
            >
              <Tab label="Request" {...a11yProps(0)} />
              <Tab label="Response" {...a11yProps(1)} disabled={!result.id} />
            </Tabs>
          </AppBar>
          <TabPanel className="tabpanel" value={tab} index={0}>
            <SyntaxHighlighter
              language="bash"
              showLineNumbers
              style={prismTheme}
            >
              {codeString}
            </SyntaxHighlighter>
          </TabPanel>
          <TabPanel className="tabpanel" value={tab} index={1}>
            <SyntaxHighlighter
              language="javascript"
              showLineNumbers
              style={prismTheme}
            >
              {JSON.stringify(result, null, "\t")}
            </SyntaxHighlighter>
          </TabPanel>
        </div>
      </div>
    </div>
  );
}
